import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { TenantService } from '@foxera/feature/tenant/services/tenant.service';
import { ApplicationRoutes, SWITCH_WORKSPACE_QUERY_PARAM } from '@foxera/shared/constants/application.routes';
import { STORAGE_KEYS } from '@foxera/shared/constants/storage-keys.constants';
import { IUserInfo } from '@foxera/shared/interfaces/user-info.interface';
import { LocalStorageService } from '@foxera/shared/services/local-storage.service';
import { TokenService } from '@foxera/shared/services/token.service';
import { firstValueFrom } from 'rxjs';

export const canActivateTenantSelection: CanActivateFn = async (route) => {
  const tenantService = inject(TenantService);
  const tokenService = inject(TokenService);
  const localStorageService = inject(LocalStorageService);
  const router = inject(Router);

  // Opened from the profile menu: always show the picker, even with a single tenant.
  if (route.queryParamMap.get(SWITCH_WORKSPACE_QUERY_PARAM) === 'true') {
    return true;
  }

  // Check if tenant is already selected
  const selectedTenant = tenantService.getSelectedTenantFromStorage();
  if (selectedTenant) {
    // Tenant already selected, redirect to default route
    router.navigate([ApplicationRoutes.SCHEDULE]);
    return false;
  }

  // Get user info and parse tenant IDs
  const userInfo = tokenService.getStoredUserInfo();
  if (!userInfo?.tenant_ids) {
    // No tenant IDs found, allow route to proceed (will show error in component)
    return true;
  }

  const tenantIds = parseTenantIds(userInfo);
  if (tenantIds.length === 0) {
    // No valid tenant IDs, allow route to proceed (will show error in component)
    return true;
  }

  try {
    // Fetch tenants
    const tenants = await firstValueFrom(tenantService.getTenantsByIds(tenantIds));

    // If only one tenant, auto-select and redirect
    if (tenants.length === 1) {
      const tenant = tenants[0];
      tenantService.setSelectedTenant(tenant);
      localStorageService.setItem(STORAGE_KEYS.SELECTED_TENANT, tenant);
      router.navigate([ApplicationRoutes.SCHEDULE]);
      return false; // Prevent route activation
    }

    // Multiple tenants, allow route to proceed to show selection screen
    return true;
  } catch {
    // Error fetching tenants, allow route to proceed (will show error in component)
    return true;
  }
};

function parseTenantIds(userInfo: IUserInfo): string[] {
  if (!userInfo?.tenant_ids) {
    return [];
  }

  return userInfo.tenant_ids
    .split(',')
    .map((id) => id.trim())
    .filter((id) => id.length > 0);
}

